/**
 * useFileUpload — presign → S3 PUT (maybe multipart) → finalize. Returns
 * { uploads, pickFiles, removeUpload } so the composer can render
 * AttachmentChip rows + send `attachmentIds` along with the message.
 *
 * Endpoints (same contract as the host app's `upload-to-conversation.ts`):
 *   POST /api/conversations/:id/attachments/presign
 *     → { kind: 'single', s3Key, uploadUrl } | { kind: 'multipart', s3Key, uploadId, partSize }
 *   PUT  <uploadUrl>            (browser → S3, Content-Type forwarded)
 *   POST /api/conversations/:id/attachments/parts/presign   (multipart only)
 *   POST /api/conversations/:id/attachments/finalize
 *     → 201 with the persisted MessageAttachment row (signed fileUrl).
 *
 * [PROD-90/93] This hook previously spoke a `/api/recall/...` +
 * presign-creates-the-row protocol that never existed server-side, so
 * every embed upload 404'd at presign. It now rides the same
 * `s3-upload.ts` protocol helper as the host app and keeps the
 * finalize row (`persisted`) so the optimistic bubble can render the
 * attachment immediately.
 *
 * Loud failure logging: every drop posts a console.error so Faro
 * picks it up, AND reports to the server's
 *   POST /api/conversations/:id/attachments/upload-failed
 * endpoint so prod failures land in Loki under
 * `app="agent-express" event="attachment.upload.failed"`. The
 * canonical fix path for `reason="cors"` is documented at
 * `apps/agent/docs/s3-cors.md`.
 */

import { useCallback, useState } from 'react'

import { ApiError, apiFetch } from '../lib/api-client'
import type { ChatAttachment } from '../types'
import { S3UploadError as S3ProtocolError, type S3UploadFlow, uploadFileToS3 } from './s3-upload'

export type UploadStatus = 'uploading' | 'done' | 'error' | 'denied'

/** Distinct failure modes for an S3 PUT. Keep in lockstep with the
 *  reason field expected by /attachments/upload-failed (server-side
 *  zod schema in apps/agent/server/src/routes/attachments.ts). */
export type S3UploadReason = 'cors' | 'http' | 'network' | 'etag-missing' | 'presign' | 'finalize'

export class S3UploadError extends Error {
  reason: S3UploadReason
  status?: number
  constructor(reason: S3UploadReason, message: string, status?: number) {
    super(message)
    this.name = 'S3UploadError'
    this.reason = reason
    this.status = status
  }
}

/** The MessageAttachment row finalize returns (fileUrl pre-signed). */
export interface PersistedAttachment extends ChatAttachment {
  conversationId: string
  messageId: string | null
  createdAt: string
}

export interface UploadState {
  localId: string
  file: File
  progress: number
  status: UploadStatus
  attachmentId?: string
  /** Finalize row — lets the send path render the attachment optimistically. */
  persisted?: PersistedAttachment
  error?: string
  reason?: S3UploadReason
}

interface UseFileUploadOpts {
  /** Lazily provides the conversation id — engine creates one on first send. */
  ensureConversation: () => Promise<string | null>
}

export function useFileUpload({ ensureConversation }: UseFileUploadOpts) {
  const [uploads, setUploads] = useState<UploadState[]>([])

  const pickFiles = useCallback(
    async (files: File[]) => {
      const conversationId = await ensureConversation()
      if (!conversationId) {
        console.warn('[agent-embed/file-upload] cannot upload — no active conversation')
        return
      }
      for (const file of files) {
        const localId = `up_${Date.now()}_${Math.random().toString(36).slice(2)}`
        setUploads((prev) => [...prev, { localId, file, progress: 0, status: 'uploading' }])
        void uploadOne({ conversationId, file, localId, setUploads })
      }
    },
    [ensureConversation],
  )

  const removeUpload = useCallback((localId: string) => {
    setUploads((prev) => prev.filter((u) => u.localId !== localId))
  }, [])

  const reset = useCallback(() => setUploads([]), [])

  return { uploads, pickFiles, removeUpload, reset }
}

async function uploadOne({
  conversationId,
  file,
  localId,
  setUploads,
}: {
  conversationId: string
  file: File
  localId: string
  setUploads: React.Dispatch<React.SetStateAction<UploadState[]>>
}) {
  try {
    let finalized: PersistedAttachment | null = null
    const flow: S3UploadFlow = {
      presign: async (meta) => {
        try {
          return await apiFetch(`/api/conversations/${conversationId}/attachments/presign`, {
            method: 'POST',
            body: JSON.stringify(meta),
          })
        } catch (presignErr) {
          throw toUploadError('presign', presignErr)
        }
      },
      presignPart: async (s3Key, uploadId, partNumber) => {
        const { url } = await apiFetch<{ url: string }>(
          `/api/conversations/${conversationId}/attachments/parts/presign`,
          { method: 'POST', body: JSON.stringify({ s3Key, uploadId, partNumber }) },
        )
        return url
      },
      finalize: async (meta) => {
        try {
          finalized = await apiFetch<PersistedAttachment>(`/api/conversations/${conversationId}/attachments/finalize`, {
            method: 'POST',
            body: JSON.stringify(meta),
          })
        } catch (finalizeErr) {
          throw toUploadError('finalize', finalizeErr)
        }
      },
    }

    await uploadFileToS3(file, flow, {
      onProgress: (pct) => {
        setUploads((prev) => prev.map((u) => (u.localId === localId ? { ...u, progress: pct } : u)))
      },
    })
    if (!finalized) {
      throw new S3UploadError('finalize', 'finalize returned no attachment row')
    }
    const row = finalized as PersistedAttachment
    setUploads((prev) =>
      prev.map((u) =>
        u.localId === localId ? { ...u, status: 'done', progress: 100, attachmentId: row.id, persisted: row } : u,
      ),
    )
  } catch (err) {
    // 415 mime-denied gets its own status so the chip can say "type not
    // allowed" rather than a generic failure.
    if (err instanceof S3UploadError && err.status === 415) {
      setUploads((prev) =>
        prev.map((u) => (u.localId === localId ? { ...u, status: 'denied', error: err.message } : u)),
      )
      return
    }
    const normalized = err instanceof S3ProtocolError ? new S3UploadError(err.reason, err.message, err.status) : err
    const reason: S3UploadReason = normalized instanceof S3UploadError ? normalized.reason : 'http'
    const status = normalized instanceof S3UploadError ? normalized.status : undefined
    const message = (normalized as Error).message
    // console.error so Faro picks it up — console.warn is filtered by
    // some Faro defaults.
    console.error(
      `[agent-embed/file-upload] attachment=${localId} reason=${reason} status=${status ?? '-'} err=${message}`,
    )
    setUploads((prev) =>
      prev.map((u) => (u.localId === localId ? { ...u, status: 'error', error: message, reason } : u)),
    )
    // Server-side report → Loki. Fire-and-forget; never re-throw the
    // report error or we'd shadow the original failure.
    void apiFetch(`/api/conversations/${conversationId}/attachments/upload-failed`, {
      method: 'POST',
      body: JSON.stringify({
        reason,
        status,
        message,
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type || 'application/octet-stream',
      }),
    }).catch(() => {
      /* Loki best-effort; client console + Faro already captured it. */
    })
  }
}

function toUploadError(phase: 'presign' | 'finalize', err: unknown): S3UploadError {
  const status = err instanceof ApiError ? err.status : undefined
  return new S3UploadError(phase, `${phase} failed: ${(err as Error).message}`, status)
}
